import { ReactNode } from "react";
import { UnsavedChangesDialog } from "./unsaved-changes-dialog";
import { useUnsavedChangesGuard } from "./use-unsaved-changes-guard";

interface NavigationGuardProviderProps {
  /** Children to render */
  children: ReactNode;
  /** Whether there are unsaved changes */
  hasUnsavedChanges: boolean;
  /** Callback to save changes */
  onSave: () => void | Promise<void>;
  /** Callback to discard changes */
  onDiscard: () => void | Promise<void>;
  /** Whether currently saving */
  saving?: boolean;
  /** Custom dialog title */
  title?: string;
  /** Custom dialog description */
  description?: string;
  /** Whether to enable the guard */
  enabled?: boolean;
}

/**
 * Wrapper component that provides navigation guard functionality
 *
 * @example
 * ```tsx
 * <NavigationGuardProvider
 *   hasUnsavedChanges={form.formState.isDirty}
 *   onSave={save}
 *   onDiscard={discard}
 *   saving={saving}
 * >
 *   <YourPageContent />
 * </NavigationGuardProvider>
 * ```
 */
export function NavigationGuardProvider({
  children,
  hasUnsavedChanges,
  onSave,
  onDiscard,
  saving = false,
  title,
  description,
  enabled = true,
}: NavigationGuardProviderProps) {
  const navigationGuard = useUnsavedChangesGuard(hasUnsavedChanges, { enabled });

  return (
    <>
      {children}
      <UnsavedChangesDialog
        open={navigationGuard.showConfirmation}
        onCancel={navigationGuard.cancel}
        onDiscard={async () => {
          await onDiscard();
          navigationGuard.confirm();
        }}
        onSave={async () => {
          try {
            await onSave();
            navigationGuard.confirm();
          } catch {
            navigationGuard.cancel();
          }
        }}
        saving={saving}
        title={title}
        description={description}
      />
    </>
  );
}
